import React, { useState, useEffect } from 'react';
import {
  Container, Card, CardContent, Table, TableBody, TableCell, TableContainer,
  TableHead, TableRow, Button, Dialog, DialogTitle, DialogContent, DialogActions,
  TextField, Select, MenuItem, FormControl, InputLabel, Alert, Checkbox,
  FormControlLabel, IconButton, Typography, Box, CircularProgress,
  InputAdornment, Paper
} from '@mui/material';
import Slider from 'react-slick';
import 'slick-carousel/slick/slick.css';
import 'slick-carousel/slick/slick-theme.css';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';

// Custom theme colors
const theme = {
  primary: '#B43361',
  secondary: '#F3D4D4',
  background: '#FFFFFF',
  text: '#000000',
};

// Custom slider styles
const customSliderStyles = `
  .slick-dots li {
    margin: 0 4px;
  }
  .slick-dots li button {
    background: transparent !important;
    width: 10px;
    height: 10px;
  }
  .slick-dots li button:before {
    content: '•';
    font-size: 12px;
    color: ${theme.text} !important;
    background: transparent !important;
    opacity: 0.6;
    width: 10px;
    height: 10px;
    line-height: 10px;
    text-align: center;
  }
  .slick-dots li.slick-active button:before {
    opacity: 1;
    color: ${theme.primary} !important;
    background: transparent !important;
  }
  .slick-slider {
    max-width: 100%;
    margin: 0 auto;
  }
  .slick-slide img {
    display: block;
    margin: 0 auto;
    max-height: 60vh;
    object-fit: contain;
  }
  .slick-prev, .slick-next {
    z-index: 1;
    width: 40px;
    height: 40px;
    background: ${theme.primary} !important;
    border-radius: 50%;
  }
  .slick-prev:hover, .slick-next:hover {
    background: ${theme.secondary} !important;
  }
  .slick-prev:before, .slick-next:before {
    color: ${theme.background} !important;
    font-size: 24px;
  }
  .slick-prev {
    left: 10px;
  }
  .slick-next {
    right: 10px;
  }
`;

const API_BASE_URL = 'https://bosscrowns-api-a228488a1e46.herokuapp.com/bosscrowns/colorIndex';

const BossCrownsColorIndexManager = () => {
  const [colorIndex, setColorIndex] = useState([]);
  const [filteredColors, setFilteredColors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tableError, setTableError] = useState(null);
  const [formError, setFormError] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [passwordModalOpen, setPasswordModalOpen] = useState(true);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState(null);
  const [selectedImages, setSelectedImages] = useState([]);
  const [isEditMode, setIsEditMode] = useState(false);
  const [selectedBrand, setSelectedBrand] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');
  const [currentItem, setCurrentItem] = useState({
    id: '',
    name: '',
    collection: '',
    code: '',
    video: '',
    image: [],
    tone: '',
    rooted: false,
    highlighted: false,
    description: '',
    brand: '',
    products: [],
  });

  const carouselSettings = {
    dots: true,
    infinite: true,
    speed: 500,
    slidesToShow: 1,
    slidesToScroll: 1,
    arrows: true,
    centerMode: true,
    centerPadding: '0px',
  };

  // Verify password
  const verifyPassword = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/verify-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ password }),
      });
      const data = await response.json();
      if (data.success) {
        setPasswordModalOpen(false);
        setPasswordError(null);
        fetchData();
      } else {
        setPasswordError(data.message || 'Invalid password');
      }
    } catch (error) {
      setPasswordError('Server error');
    }
  };

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true);
      setTableError(null);
      const response = await fetch(API_BASE_URL);
      if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
      const data = await response.json();
      setColorIndex(data);
      setFilteredColors(data);
      setLoading(false);
    } catch (err) {
      setTableError(err.message);
      setLoading(false);
    }
  };

  useEffect(() => {
    // Data will be fetched after password verification
  }, []);

  const brands = ['All', ...[...new Set(colorIndex.map((color) => color.brand))].sort()];

  useEffect(() => {
    const filtered = colorIndex.filter((color) => {
      const matchesBrand = selectedBrand === 'All' || color.brand === selectedBrand;
      const searchLower = searchQuery.toLowerCase();
      const matchesSearch =
        color.name.toLowerCase().includes(searchLower) ||
        color.collection.toLowerCase().includes(searchLower);
      return matchesBrand && matchesSearch;
    });
    setFilteredColors(filtered);
  }, [colorIndex, selectedBrand, searchQuery]);

  const openAddModal = () => {
    setIsEditMode(false);
    setCurrentItem({
      id: '',
      name: '',
      collection: '',
      code: '',
      video: '',
      image: [],
      tone: '',
      rooted: false,
      highlighted: false,
      description: '',
      brand: '',
      products: [],
    });
    setModalOpen(true);
    setFormError(null);
  };

  const openEditModal = (item) => {
    setIsEditMode(true);
    setCurrentItem({
      ...item,
      image: Array.isArray(item.image) ? item.image : item.image ? [item.image] : [],
    });
    setModalOpen(true);
    setFormError(null);
  };

  const openImageModal = (images) => {
    setSelectedImages(Array.isArray(images) ? images : images ? [images] : []);
    setImageModalOpen(true);
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setCurrentItem((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleImageChange = (index, value) => {
    const newImages = [...currentItem.image];
    newImages[index] = value;
    setCurrentItem((prev) => ({ ...prev, image: newImages }));
  };

  const addImageField = () => {
    setCurrentItem((prev) => ({ ...prev, image: [...prev.image, ''] }));
  };

  const removeImageField = (index) => {
    setCurrentItem((prev) => ({
      ...prev,
      image: prev.image.filter((_, i) => i !== index),
    }));
  };

  const handleProductsChange = (e) => {
    const products = e.target.value.split(',').map((item) => item.trim()).filter(Boolean);
    setCurrentItem((prev) => ({ ...prev, products }));
  };

  const saveItem = async () => {
    try {
      const requiredFields = ['id', 'name', 'collection', 'code', 'tone', 'description', 'brand'];
      const missingFields = requiredFields.filter(
        (field) => !currentItem[field] || currentItem[field].toString().trim() === ''
      );
      if (missingFields.length > 0) {
        setFormError(`Please fill in all required fields: ${missingFields.join(', ')}`);
        return;
      }

      for (const url of currentItem.image) {
        if (url && !/^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(url)) {
          setFormError(`Invalid image URL: ${url}`);
          return;
        }
      }

      if (typeof currentItem.rooted !== 'boolean' || typeof currentItem.highlighted !== 'boolean') {
        setFormError('Rooted and Highlighted must be selected or unselected');
        return;
      }

      const url = isEditMode ? `${API_BASE_URL}/${currentItem.id}` : API_BASE_URL;
      const method = isEditMode ? 'PUT' : 'POST';
      const { _id, createdAt, ...payload } = currentItem;

      const response = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || `HTTP error! Status: ${response.status}`);
      }

      await fetchData();
      setModalOpen(false);
      setFormError(null);
    } catch (err) {
      setFormError(err.message);
    }
  };

  const deleteItem = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        const response = await fetch(`${API_BASE_URL}/${id}`, {
          method: 'DELETE',
        });
        if (!response.ok) throw new Error(`HTTP error! Status: ${response.status}`);
        await fetchData();
        setTableError(null);
      } catch (err) {
        setTableError(err.message);
      }
    }
  };

  const handleImageError = (e) => {
    e.target.style.display = 'none';
    e.target.nextSibling.style.display = 'block';
  };

  return (
    <Container maxWidth="lg" sx={{ py: 5, bgcolor: theme.background }}>
      <style>{customSliderStyles}</style>

      {/* Password Modal */}
      <Dialog open={passwordModalOpen} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: theme.primary, color: theme.background }}>
          Enter Admin Password
        </DialogTitle>
        <DialogContent sx={{ bgcolor: theme.background }}>
          {passwordError && <Alert severity="error">{passwordError}</Alert>}
          <TextField
            fullWidth
            type="password"
            label="Password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            margin="normal"
            sx={{
              '& .MuiOutlinedInput-root': {
                '&.Mui-focused fieldset': {
                  borderColor: theme.primary,
                },
              },
              '& .MuiInputLabel-root.Mui-focused': {
                color: theme.primary,
              },
            }}
          />
        </DialogContent>
        <DialogActions sx={{ bgcolor: theme.background }}>
          <Button
            onClick={verifyPassword}
            variant="contained"
            sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* Main Content */}
      {!passwordModalOpen && (
        <Card sx={{ boxShadow: 3, bgcolor: theme.background }}>
          <CardContent>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
              <Typography variant="h4" sx={{ color: theme.text }}>
                Boss Crowns Color Index Manager
              </Typography>
              <Button
                variant="contained"
                startIcon={<AddIcon />}
                onClick={openAddModal}
                sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
              >
                Add New
              </Button>
            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
              <TextField
                variant="outlined"
                placeholder="Search by Name or Collection"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{ maxWidth: 400 }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Typography sx={{ color: theme.text }}>Search</Typography>
                    </InputAdornment>
                  ),
                  sx: {
                    '& .MuiOutlinedInput-root': {
                      '&.Mui-focused fieldset': {
                        borderColor: theme.primary,
                      },
                    },
                  },
                }}
              />
              <FormControl sx={{ minWidth: 200 }}>
                <InputLabel sx={{ '&.Mui-focused': { color: theme.primary } }}>
                  Filter by Brand
                </InputLabel>
                <Select
                  value={selectedBrand}
                  onChange={(e) => setSelectedBrand(e.target.value)}
                  sx={{
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.text,
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                      borderColor: theme.primary,
                    },
                  }}
                >
                  {brands.map((brand) => (
                    <MenuItem key={brand} value={brand}>
                      {brand}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>

            {loading && <CircularProgress sx={{ color: theme.primary }} />}
            {tableError && <Alert severity="error">Error: {tableError}</Alert>}

            {!loading && !tableError && (
              <TableContainer component={Paper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Images</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Collection</TableCell>
                      <TableCell>Code</TableCell>
                      <TableCell>Brand</TableCell>
                      <TableCell>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filteredColors.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6} align="center">
                          No colors available.
                        </TableCell>
                      </TableRow>
                    ) : (
                      filteredColors.map((color) => (
                        <TableRow key={color.id}>
                          <TableCell>
                            {Array.isArray(color.image) && color.image.length > 0 ? (
                              <>
                                <img
                                  src={color.image[0]}
                                  alt={`${color.name} thumbnail`}
                                  style={{ width: '50px', height: '50px', objectFit: 'cover', cursor: 'pointer' }}
                                  onClick={() => openImageModal(color.image)}
                                  onError={handleImageError}
                                />
                                <span style={{ display: 'none' }}>No image</span>
                                {color.image.length > 1 && (
                                  <Typography variant="caption" sx={{ ml: 1, color: theme.text }}>
                                    +{color.image.length - 1} more
                                  </Typography>
                                )}
                              </>
                            ) : (
                              'No images'
                            )}
                          </TableCell>
                          <TableCell>{color.name}</TableCell>
                          <TableCell>{color.collection}</TableCell>
                          <TableCell>{color.code}</TableCell>
                          <TableCell>{color.brand}</TableCell>
                          <TableCell>
                            <IconButton
                              onClick={() => openEditModal(color)}
                              sx={{ color: theme.primary }}
                            >
                              <EditIcon />
                            </IconButton>
                            <IconButton
                              onClick={() => deleteItem(color.id)}
                              sx={{ color: theme.text }}
                            >
                              <DeleteIcon />
                            </IconButton>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            )}

            {/* Add/Edit Modal */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="lg" fullWidth>
              <DialogTitle sx={{ bgcolor: theme.primary, color: theme.background }}>
                {isEditMode ? 'Edit Color Index' : 'Add New Color Index'}
              </DialogTitle>
              <DialogContent sx={{ bgcolor: theme.background }}>
                {formError && <Alert severity="error">{formError}</Alert>}
                <Box component="form" sx={{ mt: 2 }}>
                  <TextField
                    fullWidth
                    label="ID (You can use SKU or Make one Up) *"
                    name="id"
                    value={currentItem.id}
                    onChange={handleInputChange}
                    disabled={isEditMode}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Name *"
                    name="name"
                    value={currentItem.name}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Collection *"
                    name="collection"
                    value={currentItem.collection}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Code *"
                    name="code"
                    value={currentItem.code}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Brand *"
                    name="brand"
                    value={currentItem.brand}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Tone *"
                    name="tone"
                    value={currentItem.tone}
                    onChange={handleInputChange}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Video URL"
                    name="video"
                    value={currentItem.video}
                    onChange={handleInputChange}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <Typography variant="subtitle1" sx={{ mt: 2, color: theme.text }}>
                    Image URLs
                  </Typography>
                  {currentItem.image.map((image, index) => (
                    <Box key={index} display="flex" alignItems="center" mb={1}>
                      <TextField
                        fullWidth
                        value={image}
                        onChange={(e) => handleImageChange(index, e.target.value)}
                        placeholder={`Image URL ${index + 1}`}
                        error={image && !/^(https?:\/\/[^\s$.?#].[^\s]*)$/.test(image)}
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '&.Mui-focused fieldset': {
                              borderColor: theme.primary,
                            },
                          },
                          '& .MuiInputLabel-root.Mui-focused': {
                            color: theme.primary,
                          },
                        }}
                      />
                      <IconButton
                        onClick={() => removeImageField(index)}
                        sx={{ ml: 1, color: theme.text }}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Box>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addImageField}
                    sx={{ mt: 1, color: theme.primary, borderColor: theme.primary }}
                  >
                    Add Image URL
                  </Button>
                  <TextField
                    fullWidth
                    label="Description *"
                    name="description"
                    value={currentItem.description}
                    onChange={handleInputChange}
                    multiline
                    rows={4}
                    required
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <TextField
                    fullWidth
                    label="Products (comma-separated)"
                    name="products"
                    value={currentItem.products.join(', ')}
                    onChange={handleProductsChange}
                    margin="normal"
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '&.Mui-focused fieldset': {
                          borderColor: theme.primary,
                        },
                      },
                      '& .MuiInputLabel-root.Mui-focused': {
                        color: theme.primary,
                      },
                    }}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="rooted"
                        checked={currentItem.rooted}
                        onChange={handleInputChange}
                        sx={{
                          color: theme.primary,
                          '&.Mui-checked': {
                            color: theme.primary,
                          },
                        }}
                      />
                    }
                    label="Rooted *"
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        name="highlighted"
                        checked={currentItem.highlighted}
                        onChange={handleInputChange}
                        sx={{
                          color: theme.primary,
                          '&.Mui-checked': {
                            color: theme.primary,
                          },
                        }}
                      />
                    }
                    label="Highlighted *"
                  />
                  <Typography variant="caption" sx={{ color: theme.text }}>
                    * Required fields
                  </Typography>
                </Box>
              </DialogContent>
              <DialogActions sx={{ bgcolor: theme.background }}>
                <Button
                  onClick={saveItem}
                  variant="contained"
                  sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
                >
                  {isEditMode ? 'Update' : 'Add'}
                </Button>
                <Button
                  onClick={() => setModalOpen(false)}
                  variant="outlined"
                  sx={{ color: theme.text, borderColor: theme.text }}
                >
                  Cancel
                </Button>
              </DialogActions>
            </Dialog>

            {/* Image Viewer Modal */}
            <Dialog
              open={imageModalOpen}
              onClose={() => setImageModalOpen(false)}
              maxWidth="lg"
              sx={{ '& .MuiDialog-paper': { bgcolor: 'transparent' } }}
            >
              <DialogTitle sx={{ bgcolor: theme.primary, color: theme.background }}>
                View Images
              </DialogTitle>
              <DialogContent sx={{ p: 0, bgcolor: theme.text }}>
                {selectedImages.length > 0 ? (
                  <Slider {...carouselSettings}>
                    {selectedImages.map((image, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: 'flex',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }}
                      >
                        <img
                          src={image}
                          alt={`Color image ${index + 1}`}
                          style={{ maxWidth: '100%', maxHeight: '60vh', objectFit: 'contain' }}
                          onError={(e) =>
                            (e.target.outerHTML = `<p style="color: ${theme.background};">Error loading image</p>`)
                          }
                        />
                      </Box>
                    ))}
                  </Slider>
                ) : (
                  <Typography sx={{ color: theme.background, textAlign: 'center' }}>
                    No images available
                  </Typography>
                )}
              </DialogContent>
              <DialogActions sx={{ bgcolor: theme.text }}>
                <Button
                  onClick={() => setImageModalOpen(false)}
                  variant="contained"
                  sx={{ bgcolor: theme.primary, '&:hover': { bgcolor: theme.secondary } }}
                >
                  Close
                </Button>
              </DialogActions>
            </Dialog>
          </CardContent>
        </Card>
      )}
    </Container>
  );
};

export default BossCrownsColorIndexManager;